import { useFormikContext } from "formik";
import { cloneDeep, isEqual, omit } from "lodash";
import { useEffect, useRef, useState, useCallback } from "react";

export const useAutosaveReport = ({ onAutosave, isFetching = false, ignoreFields = [] }) => {
  const delay = 60000;
  const { values, dirty } = useFormikContext();
  const timer = useRef(null);
  const lastSavedValues = useRef(null);
  const isSaving = useRef(false);
  const [isAutosaveRefetch, setIsAutosaveRefetch] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  // Extract normalize function for reuse
  const normalize = useCallback(
    (v) => (v ? omit(cloneDeep(v), ["saveForLater", "isAutoSave", "emailFormData", ...ignoreFields]) : v),
    [ignoreFields]
  );

  const triggerAutosave = useCallback(async () => {
    if (isSaving.current || isFetching) return;

    isSaving.current = true;
    setShowOverlay(true);

    try {
      const autosavePayload = {
        ...cloneDeep(values),
        saveForLater: true,
        isAutoSave: true,
      };

      await onAutosave(autosavePayload);
      lastSavedValues.current = normalize(values);
      setIsAutosaveRefetch(true);
    } catch (err) {
      console.error("Autosave failed", err);
    } finally {
      isSaving.current = false;
    }
  }, [values, onAutosave, isFetching, normalize]);

  // Reset overlay after fetch completes
  useEffect(() => {
    if (!isFetching && !isSaving.current) {
      const timeout = setTimeout(() => {
        setIsAutosaveRefetch(false);
        setShowOverlay(false);
      }, 250);
      return () => clearTimeout(timeout);
    }
  }, [isFetching, isSaving.current]);

  // Auto trigger on value change
  useEffect(() => {
    if (!dirty) return;
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(async () => {
      const newValues = normalize(values);
      const oldValues = normalize(lastSavedValues.current);

      if (isEqual(newValues, oldValues)) {
        console.log("formik autosave values same, skipping save");
        return;
      }

      await triggerAutosave();
    }, delay);

    return () => clearTimeout(timer.current);
  }, [values, dirty, delay, triggerAutosave, normalize]);

  return {
    showOverlay: showOverlay || isSaving.current || isAutosaveRefetch,
    triggerAutosave,
  };
};
